import { useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { RichText } from '@/components/RichText'
import { platformApi } from '../api'
import { MAX_OVERRIDE_REASON, formatSize, saveBlob, validateOverrideReason } from '../lib/files'
import type { Answers, FileAccess, FileRecord, FormDefinition } from '../types'
import { activePages, displayAnswer, fieldVisible, isAnswered } from '../lib/form'

interface Props {
    definition: FormDefinition
    answers: Answers
    /** Only list questions that have an answer (default: also show unanswered). */
    answeredOnly?: boolean
    /** Resolve a user_reference id to a display name. */
    userName?: (id: string) => string
    /** File records for this submission (resolves file answers to names). */
    files?: FileRecord[]
    submissionId?: string
    fileAccess?: FileAccess
}

const isHttpUrl = (v: string) => /^https?:\/\//i.test(v.trim())

/** The file answers of one question: downloadable names, or a legacy link. */
function FileAnswer({ raw, files, onOpen, canOpen }: {
    raw: unknown
    files: FileRecord[]
    canOpen: boolean
    onOpen: (f: FileRecord) => void
}) {
    if (typeof raw === 'string') {
        return isHttpUrl(raw)
            ? <a href={raw.trim()} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">{raw}</a>
            : <>{raw}</>
    }
    const ids = Array.isArray(raw) ? (raw as unknown[]).map(String) : []
    return (
        <ul className="flex flex-col gap-1">
            {ids.map((id) => {
                const f = files.find((x) => x.id === id)
                if (!f) return <li key={id} className="text-muted-foreground">File not available</li>
                const label = `${f.name} (${formatSize(f.size_bytes)})`
                return (
                    <li key={id}>
                        {canOpen ? (
                            <Button type="button" variant="link" className="h-auto whitespace-normal p-0 text-left" onClick={() => onOpen(f)}>
                                {label}
                            </Button>
                        ) : label}
                    </li>
                )
            })}
        </ul>
    )
}

function OverrideDialog({ file, submissionId, onClose }: { file: FileRecord | null; submissionId: string; onClose: () => void }) {
    const { user } = useAuth()
    const minLen = user?.uploads?.minOverrideLen ?? 10
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const check = validateOverrideReason(reason, minLen)

    const close = () => { setReason(''); setError(null); onClose() }
    const open = async () => {
        if (!file || !check.ok) return
        setBusy(true)
        setError(null)
        try {
            const { blob, filename } = await platformApi.downloadFile(submissionId, file.id, reason.trim())
            saveBlob(blob, filename || file.name)
            close()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not open the file')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog open={file !== null} onOpenChange={(o) => { if (!o && !busy) close() }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Open file as administrator</DialogTitle>
                    <DialogDescription>
                        You are not the submitter or a reviewer of this request. Opening this file is recorded in the audit log.
                    </DialogDescription>
                </DialogHeader>
                <Field data-invalid={error ? true : undefined}>
                    <FieldLabel htmlFor="override-reason">Reason</FieldLabel>
                    <Textarea id="override-reason" rows={3} maxLength={MAX_OVERRIDE_REASON} value={reason}
                        aria-invalid={error ? true : undefined} onChange={(e) => setReason(e.target.value)} />
                    <FieldDescription className="text-xs">
                        {check.remaining > 0
                            ? `${check.remaining} more ${check.remaining === 1 ? 'character' : 'characters'} required`
                            : `${check.length}/${MAX_OVERRIDE_REASON}`}
                    </FieldDescription>
                    {error && <FieldError>{error}</FieldError>}
                </Field>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={close} disabled={busy}>Cancel</Button>
                    <Button type="button" onClick={open} disabled={!check.ok || busy}>
                        {busy && <Spinner data-icon="inline-start" />}Open file
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** Read-only summary of a submission, grouped by page, honouring visibility. */
export function AnswersView({ definition, answers, answeredOnly = false, userName, files = [], submissionId, fileAccess = 'none' }: Props) {
    const [overrideFile, setOverrideFile] = useState<FileRecord | null>(null)
    const [openError, setOpenError] = useState<string | null>(null)
    const canOpen = Boolean(submissionId) && fileAccess !== 'none'
    const openFile = async (f: FileRecord) => {
        if (!submissionId) return
        setOpenError(null)
        if (fileAccess === 'override') { setOverrideFile(f); return }
        try {
            const { blob, filename } = await platformApi.downloadFile(submissionId, f.id)
            saveBlob(blob, filename || f.name)
        } catch (e) {
            setOpenError(e instanceof Error ? e.message : 'Could not open the file')
        }
    }
    const pages = activePages(definition, answers)
    return (
        <div className="flex flex-col gap-8">
            {openError && <p role="alert" className="text-sm text-destructive">{openError}</p>}
            {submissionId && <OverrideDialog file={overrideFile} submissionId={submissionId} onClose={() => setOverrideFile(null)} />}
            {pages.map((p) => {
                const fields = p.fields.filter((f) => f.type !== 'html' && fieldVisible(f, answers))
                    .filter((f) => !answeredOnly || isAnswered(answers[f.key]))
                if (fields.length === 0) return null
                return (
                    <section key={p.id} className="flex flex-col gap-3">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{p.title}</h3>
                        <dl className="flex flex-col divide-y rounded-lg border bg-card">
                            {fields.map((f) => {
                                const raw = answers[f.key]
                                const text = f.type === 'user_reference' && userName && typeof raw === 'string'
                                    ? userName(raw)
                                    : displayAnswer(f, raw)
                                return (
                                    <div key={f.key} className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4">
                                        <dt className="flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground">
                                            <RichText text={f.label} />
                                        </dt>
                                        <dd className="whitespace-pre-wrap break-words text-sm text-foreground">
                                            {f.type === 'file' && isAnswered(raw)
                                                ? <FileAnswer raw={raw} files={files} canOpen={canOpen} onOpen={openFile} />
                                                : text || <span className="text-muted-foreground">—</span>}
                                        </dd>
                                    </div>
                                )
                            })}
                        </dl>
                    </section>
                )
            })}
        </div>
    )
}
